import {
  keepPreviousData,
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { inboxApi } from "./api";
import { hasAutoSent, applyAutoSentFlags } from "./autoSentRegistry";
import { useCrmContact, type CrmContact } from "./crm";
import type {
  AiReplyModeSetting,
  SearchBlockName,
  ConversationResponse,
  ConversationsResponse,
  ConversationListParams,
  CreateManualNoteRequest,
  EligibleApplication,
  Message,
  MessageChannel,
  SendChatRequest,
  SendEmailRequest,
  SendSmsRequest,
  SearchFilterParams,
  SendEmailBatchRequest,
  SendSmsBatchRequest,
  TranslateRequest
} from "../types";

const OPTIMISTIC_UNREAD_SENTINEL = 1;

// Per ticket: only these are ineligible for chat; every other status is fine.
const INELIGIBLE_CHAT_STATUSES = ["pending", "canceled", "merged"];

// Eligible applications derived from the CRM contact's applicants (no dedicated endpoint).
const deriveEligibleApplications = (
  contact: CrmContact | undefined
): EligibleApplication[] => {
  const seen = new Set<number>();
  const eligible: EligibleApplication[] = [];
  for (const applicant of contact?.applicants ?? []) {
    const app = applicant.application;
    if (!app || INELIGIBLE_CHAT_STATUSES.includes(app.status)) continue;
    if (seen.has(app.id)) continue;
    seen.add(app.id);
    eligible.push({
      application_id: app.id,
      address_label: app.property?.name ?? "",
      status: app.status,
      application_updated_at: app.updated_at ?? app.created_at ?? ""
    });
  }
  return eligible;
};

// "Invited to apply, but not started yet", without a dedicated endpoint:
//  - a lead invite creates the application right away and links it to the
//    lead (`application_id`), but the applicant row only appears once the
//    invitee opens the magic link — so a lead whose application has no
//    applicant of this contact is an outstanding invite;
//  - a co-applicant invite does create the applicant, on an application that
//    stays `pending` until they start.
export const hasOutstandingInvite = (
  contact: CrmContact | undefined
): boolean => {
  const applicants = contact?.applicants ?? [];
  if (applicants.some((a) => a.application?.status === "pending")) return true;
  const applicantAppIds = new Set(applicants.map((a) => a.application_id));
  return (contact?.leads ?? []).some(
    (lead) => lead.application_id && !applicantAppIds.has(lead.application_id)
  );
};

interface PaginatedConversations {
  pageParams: unknown[];
  pages: ConversationsResponse[];
}

const setConversationUnreadCount = (
  oldData: PaginatedConversations | undefined,
  conversationId: string,
  unreadCount: number,
  manuallyUnread = false
): PaginatedConversations | undefined => {
  if (!oldData) return oldData;
  return {
    ...oldData,
    pages: oldData.pages.map((page) => ({
      ...page,
      conversations: page.conversations.map((conv) =>
        conv.id === conversationId
          ? {
              ...conv,
              unread_count: unreadCount,
              is_manually_unread: manuallyUnread
            }
          : conv
      )
    }))
  };
};

export const inboxKeys = {
  all: ["INBOX"],
  conversations: () => [...inboxKeys.all, "CONVERSATIONS"],
  conversationsList: (filters?: ConversationListParams) => [
    ...inboxKeys.conversations(),
    "LIST",
    filters
  ],
  preferences: () => [...inboxKeys.all, "PREFERENCES"],
  unreadConversationsCount: () => [
    ...inboxKeys.conversations(),
    "UNREAD_COUNT"
  ],
  // Outside conversations() on purpose: the list-cache updaters below assume
  // the paginated list shape, and this one is a flat unread snapshot.
  unreadByContact: () => [...inboxKeys.all, "UNREAD_BY_CONTACT"],
  conversation: (id: string) => [...inboxKeys.conversations(), id],
  sendOptions: (conversationId: string) => [
    ...inboxKeys.conversation(conversationId),
    "SEND_OPTIONS"
  ],
  smsOptOuts: (conversationId: string) => [
    ...inboxKeys.conversation(conversationId),
    "SMS_OPT_OUTS"
  ],
  messages: (
    conversationId: string,
    params?: {
      limit?: number;
      channel?: MessageChannel;
      anchor?: string;
      from?: string;
      to?: string;
      q?: string;
    }
  ) => [...inboxKeys.conversation(conversationId), "MESSAGES", params],
  contacts: () => [...inboxKeys.all, "CONTACTS"],
  contactsSearch: (query: string) => [...inboxKeys.contacts(), "SEARCH", query],
  contactRelated: (contactId: string) => [
    ...inboxKeys.all,
    "CONTACTS",
    contactId,
    "RELATED"
  ],
  search: (q: string, filters?: SearchFilterParams) => [
    ...inboxKeys.all,
    "SEARCH",
    q,
    filters ?? {}
  ],
  eligibleApplications: (contactId: string) => [
    ...inboxKeys.all,
    "CONTACTS",
    contactId,
    "ELIGIBLE_APPLICATIONS"
  ],
  filterCounts: (filters?: SearchFilterParams) => [
    ...inboxKeys.all,
    "FILTER_COUNTS",
    filters ?? {}
  ],
  translate: () => [...inboxKeys.all, "TRANSLATE"]
};

export const useConversations = (
  params?: Omit<ConversationListParams, "page">
) => {
  return useInfiniteQuery({
    queryKey: inboxKeys.conversationsList(params),
    queryFn: ({ pageParam = 1 }) =>
      inboxApi.listConversations({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const { total } = lastPage.pagination;
      const fetched = allPages.flatMap((p) => p.conversations).length;
      return fetched < total ? allPages.length + 1 : undefined;
    },
    placeholderData: keepPreviousData
  });
};

export const useConversation = (id: string) => {
  return useQuery({
    queryKey: inboxKeys.conversation(id),
    queryFn: () => inboxApi.getConversation(id),
    enabled: !!id
  });
};

// Addresses the current user may send to for this conversation's contact.
// Drives the To picker so a restricted user can't pick an address they lack
// access to (backend also enforces with a 403).
export const useSendOptions = (conversationId?: string) => {
  return useQuery({
    queryKey: inboxKeys.sendOptions(conversationId ?? ""),
    queryFn: () => inboxApi.getSendOptions(conversationId as string),
    enabled: !!conversationId
  });
};

export const useEligibleApplications = (contactId: string) => {
  const { data: contact, ...rest } = useCrmContact(contactId);
  const data = useMemo(
    () => (contact ? deriveEligibleApplications(contact) : undefined),
    [contact]
  );
  return { ...rest, data };
};

export const useMessages = (
  conversationId: string,
  params?: {
    limit?: number;
    channel?: "email" | "sms" | "note" | "chat" | "voice";
    anchor?: string;
    from?: string;
    to?: string;
    q?: string;
  }
) => {
  const isAnchorMode = !!params?.anchor;

  return useInfiniteQuery({
    queryKey: inboxKeys.messages(conversationId, params),
    queryFn: ({ pageParam, direction }) => {
      // Initial fetch in anchor mode: load messages around the target

      if (pageParam === undefined && isAnchorMode)
        return inboxApi.getMessages(conversationId, {
          limit: params.limit,
          channel: params.channel,
          anchor: params.anchor,
          from: params.from,
          to: params.to,
          q: params.q
        });

      // Initial fetch in normal mode: load newest messages (no cursor)
      if (pageParam === undefined)
        return inboxApi.getMessages(conversationId, {
          limit: params?.limit,
          channel: params?.channel,
          from: params?.from,
          to: params?.to,
          q: params?.q
        });

      // Subsequent fetches: use direction from React Query
      const fetchDirection = direction === "backward" ? "before" : "after";

      return inboxApi.getMessages(conversationId, {
        limit: params?.limit,
        channel: params?.channel,
        cursor: pageParam,
        direction: fetchDirection,
        from: params?.from,
        to: params?.to,
        q: params?.q
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (isAnchorMode)
        return lastPage.has_more_after
          ? (lastPage.after_cursor ?? undefined)
          : undefined;

      // Normal mode: no forward loading (we start at newest)
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (isAnchorMode)
        return firstPage.has_more_before
          ? (firstPage.before_cursor ?? undefined)
          : undefined;

      // Non-anchor mode: use before_cursor (oldest message) for older messages
      return firstPage.has_more
        ? (firstPage.before_cursor ?? undefined)
        : undefined;
    },
    enabled: !!conversationId,
    // Re-stamp AI-auto-sent flags on every read so a refetch or pagination
    // can't downgrade the "Auto-sent by the AI leasing agent" label for a
    // message the authoritative suggestion_auto_sent event already named
    // (see autoSentRegistry). Returns the same data reference when nothing is
    // tracked — the overwhelmingly common case — so there's no render churn.
    select: (data) =>
      hasAutoSent()
        ? {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              messages: page.messages.map(applyAutoSentFlags)
            }))
          }
        : data
  });
};

// Numbers the conversation contact has currently opted out of SMS. Used by the
// SMS composer to disable/block texting those numbers. Read-only query — mirrors
// the no-toast convention of the sibling read hooks above; the composer fails
// open (send stays enabled) if this cannot load.
export const useSmsOptOuts = (conversationId: string) => {
  return useQuery({
    queryKey: inboxKeys.smsOptOuts(conversationId),
    queryFn: () => inboxApi.getSmsOptOuts(conversationId),
    enabled: !!conversationId
  });
};

export const useInboxSearch = (q: string, filters?: SearchFilterParams) =>
  useQuery({
    queryKey: inboxKeys.search(q, filters),
    queryFn: () => inboxApi.search(q, filters),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData
  });

export const useInboxFilterCounts = (
  filters: SearchFilterParams | undefined,
  enabled: boolean
) =>
  useQuery({
    queryKey: inboxKeys.filterCounts(filters),
    queryFn: () => inboxApi.filterCounts(filters),
    enabled,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData
  });

export const useLoadMoreSearchBlock = () =>
  useMutation({
    mutationKey: ["INBOX", "SEARCH", "LOAD_MORE"],
    mutationFn: ({
      q,
      block,
      offset,
      filters
    }: {
      q: string;
      block: SearchBlockName;
      offset: number;
      filters?: SearchFilterParams;
    }) => inboxApi.search(q, { ...filters, block, offset }),
    onError: (error) => toast.error(getErrorMessage(error))
  });

interface MessagePage {
  messages: unknown[];
  [key: string]: unknown;
}

export interface PaginatedMessages {
  pageParams: unknown[];
  pages: MessagePage[];
}

const chatClientMessageId = (message: unknown): string | undefined =>
  (message as Message | undefined)?.chat_details?.client_message_id ??
  undefined;

const mergeIntoExistingMessage = (
  existing: unknown,
  incoming: unknown
): unknown => {
  const prev = existing as Message;
  const next = incoming as Message;
  return {
    ...next,
    chat_details: next.chat_details ?? prev.chat_details,
    attachments: next.attachments ?? prev.attachments
  };
};

const replaceFirstMatch = (
  messages: unknown[],
  matches: (message: unknown) => boolean,
  newMessage: unknown
): unknown[] => {
  let replaced = false;
  const result: unknown[] = [];
  for (const existing of messages) {
    if (!matches(existing)) {
      result.push(existing);
      continue;
    }
    if (!replaced) {
      result.push(mergeIntoExistingMessage(existing, newMessage));
      replaced = true;
    }
  }
  return result;
};

export const upsertMessageIntoPages = (
  oldData: PaginatedMessages | undefined,
  newMessage: unknown,
  replaceId?: string
): PaginatedMessages | undefined => {
  if (!oldData) return oldData;
  const incoming = newMessage as Message;
  const incomingClientId = chatClientMessageId(newMessage);
  const matches = (existing: unknown) => {
    const msg = existing as Message;
    return (
      msg.id === incoming.id ||
      (replaceId !== undefined && msg.id === replaceId) ||
      (incomingClientId !== undefined &&
        chatClientMessageId(existing) === incomingClientId)
    );
  };
  const matchedPageIndex = oldData.pages.findIndex((page) =>
    page.messages.some(matches)
  );
  if (matchedPageIndex === -1)
    return {
      ...oldData,
      pages: oldData.pages.map((page, index) =>
        index === oldData.pages.length - 1
          ? { ...page, messages: [...page.messages, newMessage] }
          : page
      )
    };
  return {
    ...oldData,
    pages: oldData.pages.map((page, index) =>
      index === matchedPageIndex
        ? {
            ...page,
            messages: replaceFirstMatch(page.messages, matches, newMessage)
          }
        : page
    )
  };
};

const removeMessageFromPages = (
  oldData: PaginatedMessages | undefined,
  messageId: string
): PaginatedMessages | undefined => {
  if (!oldData) return oldData;
  return {
    ...oldData,
    pages: oldData.pages.map((page) => ({
      ...page,
      messages: page.messages.filter((msg) => (msg as Message).id !== messageId)
    }))
  };
};

// A messages query key ends with the params `useMessages` was called with
// ([...conversation(id), "MESSAGES", params]), while every cache write below is a
// prefix match that stops at "MESSAGES" — React Query's partialMatchKey only
// walks the keys present in the filter, so the trailing params object is
// invisible to it. A params object carrying a `channel` is a single-channel view
// (the message pop-up's chips) and must never be handed a message from another
// channel; an unfiltered cache (undefined, {}, { anchor }) takes everything.
export const messageCacheAcceptsChannel = (
  queryKey: readonly unknown[],
  channel: MessageChannel | undefined
): boolean => {
  const params = queryKey[queryKey.length - 1] as
    | { channel?: MessageChannel }
    | undefined;
  if (!params?.channel) return true;
  return params.channel === channel;
};

const setMessagesCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  updater: (
    oldData: PaginatedMessages | undefined
  ) => PaginatedMessages | undefined,
  channel?: MessageChannel
) => {
  queryClient.setQueriesData(
    {
      queryKey: [...inboxKeys.conversation(conversationId), "MESSAGES"],
      predicate: (query) => messageCacheAcceptsChannel(query.queryKey, channel)
    },
    updater
  );
};

const nextOptimisticTimestamp = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string
): string => {
  const caches = queryClient.getQueriesData<PaginatedMessages>({
    queryKey: [...inboxKeys.conversation(conversationId), "MESSAGES"]
  });
  let latest = 0;
  for (const [, data] of caches)
    for (const page of data?.pages ?? [])
      for (const message of page.messages) {
        const at = new Date((message as Message).created_at).getTime();
        if (at > latest) latest = at;
      }
  return new Date(Math.max(Date.now(), latest + 1)).toISOString();
};

const optimisticAppendMessage = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  response: { message?: unknown } | unknown,
  replaceId?: string
) => {
  const newMessage = (response as { message?: unknown }).message || response;

  setMessagesCaches(
    queryClient,
    conversationId,
    (oldData) => upsertMessageIntoPages(oldData, newMessage, replaceId),
    (newMessage as Message | undefined)?.channel
  );

  queryClient.invalidateQueries({
    queryKey: inboxKeys.conversation(conversationId),
    exact: true,
    refetchType: "active"
  });

  queryClient.invalidateQueries({
    queryKey: [...inboxKeys.conversations(), "LIST"],
    refetchType: "none"
  });
};

export const useCreateManualNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateManualNoteRequest) =>
      inboxApi.createManualNote(data),
    onSuccess: (response, variables) => {
      optimisticAppendMessage(queryClient, variables.conversation_id, response);
    }
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) => inboxApi.markAsRead(conversationId),
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({
        queryKey: [...inboxKeys.conversations(), "LIST"]
      });

      const previous = queryClient.getQueriesData<PaginatedConversations>({
        queryKey: [...inboxKeys.conversations(), "LIST"]
      });

      // Optimistic update: set unread_count to 0 in conversation list cache
      queryClient.setQueriesData(
        { queryKey: [...inboxKeys.conversations(), "LIST"] },
        (oldData: PaginatedConversations | undefined) =>
          setConversationUnreadCount(oldData, conversationId, 0)
      );

      return { previous };
    },
    onError: (_error, _conversationId, context) => {
      context?.previous.forEach(([key, data]) =>
        queryClient.setQueryData<PaginatedConversations>(key, data)
      );
    },
    onSettled: (_data, _error, conversationId) => {
      queryClient.invalidateQueries({
        queryKey: inboxKeys.conversation(conversationId),
        exact: true,
        refetchType: "active"
      });
      queryClient.invalidateQueries({
        queryKey: [...inboxKeys.conversations(), "LIST"],
        refetchType: "active"
      });
      queryClient.invalidateQueries({
        queryKey: inboxKeys.unreadConversationsCount(),
        refetchType: "active"
      });
    }
  });
};

export const useMarkAsUnread = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      inboxApi.markAsUnread(conversationId),
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({
        queryKey: [...inboxKeys.conversations(), "LIST"]
      });

      const previous = queryClient.getQueriesData<PaginatedConversations>({
        queryKey: [...inboxKeys.conversations(), "LIST"]
      });

      queryClient.setQueriesData(
        { queryKey: [...inboxKeys.conversations(), "LIST"] },
        (oldData: PaginatedConversations | undefined) =>
          setConversationUnreadCount(
            oldData,
            conversationId,
            OPTIMISTIC_UNREAD_SENTINEL,
            true
          )
      );

      return { previous };
    },
    onError: (_error, _conversationId, context) => {
      context?.previous.forEach(([key, data]) =>
        queryClient.setQueryData<PaginatedConversations>(key, data)
      );
      toast.error("Failed to mark conversation as unread");
    },
    onSettled: (_data, _error, conversationId) => {
      queryClient.invalidateQueries({
        queryKey: inboxKeys.conversation(conversationId)
      });
      queryClient.invalidateQueries({
        queryKey: [...inboxKeys.conversations(), "LIST"],
        refetchType: "active"
      });
      queryClient.invalidateQueries({
        queryKey: inboxKeys.unreadConversationsCount(),
        refetchType: "active"
      });
      queryClient.invalidateQueries({
        queryKey: inboxKeys.conversation(conversationId),
        refetchType: "active"
      });
    }
  });
};

export const useRetryEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => inboxApi.retryEmail(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: inboxKeys.all,
        refetchType: "active"
      });
    }
  });
};

export const useDeleteMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => inboxApi.deleteMessage(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: inboxKeys.all,
        refetchType: "active"
      });
    }
  });
};

// follow_schedule can't compute the schedule-driven mode locally, so
// message_mode is left as-is until the refetch settles it.
const optimisticAiReplyState = (mode: AiReplyModeSetting) => {
  switch (mode) {
    case "off":
      return { ai_suggestions_enabled: false };
    case "follow_schedule":
      return { ai_suggestions_enabled: true, ai_reply_mode_override: null };
    default:
      return {
        ai_suggestions_enabled: true,
        ai_reply_mode_override: mode,
        message_mode: mode
      };
  }
};

export const useSetAiReplyMode = (conversationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [
      ...inboxKeys.conversation(conversationId),
      "AI_REPLY_MODE",
      "UPDATE"
    ],
    mutationFn: (mode: AiReplyModeSetting) =>
      inboxApi.setAiReplyMode(conversationId, mode),
    onMutate: async (mode) => {
      const key = inboxKeys.conversation(conversationId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ConversationResponse>(key);
      queryClient.setQueryData<ConversationResponse>(key, (old) =>
        old?.conversation
          ? {
              ...old,
              conversation: {
                ...old.conversation,
                ...optimisticAiReplyState(mode)
              }
            }
          : old
      );
      return { previous };
    },
    onError: (_err, _mode, context) => {
      if (context?.previous)
        queryClient.setQueryData(
          inboxKeys.conversation(conversationId),
          context.previous
        );
      toast.error("Failed to update AI reply setting");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: inboxKeys.conversation(conversationId),
        exact: true
      });
    }
  });
};

export const useDismissSuggestion = () => {
  return useMutation({
    mutationKey: [...inboxKeys.all, "SUGGESTIONS", "DISMISS"],
    mutationFn: (anchorMessageId: string) =>
      inboxApi.dismissSuggestion(anchorMessageId),
    onError: () => {
      toast.error("Failed to dismiss suggestion");
    }
  });
};

export const useRetrySuggestion = () => {
  return useMutation({
    mutationKey: [...inboxKeys.all, "SUGGESTIONS", "RETRY"],
    mutationFn: (anchorMessageId: string) =>
      inboxApi.retrySuggestion(anchorMessageId),
    onSuccess: (data) => {
      // The backend no-ops with ok:false when there is no failed suggestion
      // for this anchor anymore (e.g. resolved in another tab) — no WS event
      // will follow, so tell the user instead of leaving a dead Retry button.
      if (!data.ok) toast.error("This suggestion is no longer available");
    },
    onError: () => {
      toast.error("Failed to retry suggestion");
    }
  });
};

// Edit-capture telemetry for AI-216 — best-effort by design: never block or
// alarm the user over a lost signal, but keep the failure observable.
export const useResolveSuggestion = () => {
  return useMutation({
    mutationKey: [...inboxKeys.all, "SUGGESTIONS", "RESOLVE"],
    mutationFn: (payload: {
      anchor_message_id: string;
      final_sent_text: string;
      outbound_message_id?: string;
    }) => inboxApi.resolveSuggestion(payload),
    onError: (err) => {
      console.warn("[inbox] Failed to record suggestion resolution:", err);
    }
  });
};

// Errors are intentionally left to the caller so the compose box / card draft
// is never mutated on failure and the inline "Try again" affordance can drive a
// retry (CRM-1092, R6).
export const useTranslateText = () =>
  useMutation({
    mutationKey: inboxKeys.translate(),
    mutationFn: (payload: TranslateRequest) => inboxApi.translate(payload)
  });

export const useSendEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendEmailRequest) => inboxApi.sendEmail(data),
    onSuccess: (response, variables) => {
      optimisticAppendMessage(queryClient, variables.conversation_id, response);
    }
  });
};

export const useSendSms = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendSmsRequest) => inboxApi.sendSms(data),
    onSuccess: (response, variables) => {
      optimisticAppendMessage(queryClient, variables.conversation_id, response);
    }
  });
};

interface BatchOptimisticCopy {
  conversation_id: string;
  channel: "email" | "sms";
  content_plain: string;
  to_address: string;
  subject?: string;
}

const insertBatchOptimisticCopy = async (
  queryClient: ReturnType<typeof useQueryClient>,
  optimistic: BatchOptimisticCopy
): Promise<string> => {
  await queryClient.cancelQueries({
    queryKey: [
      ...inboxKeys.conversation(optimistic.conversation_id),
      "MESSAGES"
    ]
  });
  const optimisticId = `optimistic-batch-${crypto.randomUUID()}`;
  const message: Message = {
    id: optimisticId,
    channel: optimistic.channel,
    direction: "outbound",
    content_plain: optimistic.content_plain,
    sender_type: "user",
    sender_id: null,
    sender_identifier: null,
    status: "sending",
    sent_at: null,
    created_at: nextOptimisticTimestamp(
      queryClient,
      optimistic.conversation_id
    ),
    ...(optimistic.channel === "email"
      ? {
          email_details: {
            subject: optimistic.subject ?? "",
            from_address: "",
            to_addresses: [optimistic.to_address]
          }
        }
      : {
          sms_details: {
            from_number: "",
            to_number: optimistic.to_address,
            segments_count: Math.ceil(optimistic.content_plain.length / 160)
          }
        }),
    attachments: []
  };
  setMessagesCaches(
    queryClient,
    optimistic.conversation_id,
    (oldData) => upsertMessageIntoPages(oldData, message),
    optimistic.channel
  );
  return optimisticId;
};

const settleBatchOptimisticCopy = (
  queryClient: ReturnType<typeof useQueryClient>,
  optimistic: BatchOptimisticCopy | undefined,
  optimisticId: string | undefined,
  outcome: "success" | "error"
) => {
  if (!optimistic || !optimisticId) return;
  if (outcome === "success") {
    queryClient.invalidateQueries({
      queryKey: [
        ...inboxKeys.conversation(optimistic.conversation_id),
        "MESSAGES"
      ]
    });
    return;
  }
  setMessagesCaches(
    queryClient,
    optimistic.conversation_id,
    (oldData) => removeMessageFromPages(oldData, optimisticId),
    optimistic.channel
  );
};

export const useSendEmailBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      optimistic: _optimistic,
      ...data
    }: SendEmailBatchRequest & { optimistic?: BatchOptimisticCopy }) =>
      inboxApi.sendEmailBatch(data),
    onMutate: async (variables) => ({
      optimisticId: variables.optimistic
        ? await insertBatchOptimisticCopy(queryClient, variables.optimistic)
        : undefined
    }),
    onSuccess: (_response, variables, context) => {
      queryClient.invalidateQueries({ queryKey: inboxKeys.conversations() });
      settleBatchOptimisticCopy(
        queryClient,
        variables.optimistic,
        context?.optimisticId,
        "success"
      );
    },
    onError: (_error, variables, context) => {
      settleBatchOptimisticCopy(
        queryClient,
        variables.optimistic,
        context?.optimisticId,
        "error"
      );
    }
  });
};

export const useSendSmsBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      optimistic: _optimistic,
      ...data
    }: SendSmsBatchRequest & { optimistic?: BatchOptimisticCopy }) =>
      inboxApi.sendSmsBatch(data),
    onMutate: async (variables) => ({
      optimisticId: variables.optimistic
        ? await insertBatchOptimisticCopy(queryClient, variables.optimistic)
        : undefined
    }),
    onSuccess: (_response, variables, context) => {
      queryClient.invalidateQueries({ queryKey: inboxKeys.conversations() });
      settleBatchOptimisticCopy(
        queryClient,
        variables.optimistic,
        context?.optimisticId,
        "success"
      );
    },
    onError: (_error, variables, context) => {
      settleBatchOptimisticCopy(
        queryClient,
        variables.optimistic,
        context?.optimisticId,
        "error"
      );
    }
  });
};

export const useSendChat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendChatRequest) => inboxApi.sendChat(data),
    onMutate: async (data) => {
      await queryClient.cancelQueries({
        queryKey: [...inboxKeys.conversation(data.conversation_id), "MESSAGES"]
      });
      const clientMessageId = data.client_message_id ?? crypto.randomUUID();
      const optimisticId = `optimistic-${clientMessageId}`;
      const optimisticMessage: Message = {
        id: optimisticId,
        channel: "chat",
        direction: "outbound",
        content_plain: data.content,
        sender_type: "user",
        sender_id: null,
        sender_identifier: null,
        status: "sending",
        sent_at: null,
        created_at: nextOptimisticTimestamp(queryClient, data.conversation_id),
        chat_details: {
          application_id: data.application_id,
          client_message_id: clientMessageId
        },
        attachments: []
      };
      setMessagesCaches(
        queryClient,
        data.conversation_id,
        (oldData) => upsertMessageIntoPages(oldData, optimisticMessage),
        "chat"
      );
      return { optimisticId };
    },
    onSuccess: (response, variables, context) => {
      optimisticAppendMessage(
        queryClient,
        variables.conversation_id,
        response,
        context?.optimisticId
      );
    },
    onError: (_error, variables, context) => {
      // Same channel as the optimistic insert above — without it the channel
      // predicate skips the chat-filtered cache and the failed bubble is stuck
      // on "sending" forever.
      if (context?.optimisticId)
        setMessagesCaches(
          queryClient,
          variables.conversation_id,
          (oldData) => removeMessageFromPages(oldData, context.optimisticId),
          "chat"
        );
    }
  });
};

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // 25 MB (server limit)

// Upload a chat attachment: request a presigned URL, PUT the file to S3, confirm.
// Returns the attachment id to pass as attachment_ids on send.
export const useUploadChatAttachment = () =>
  useMutation({
    mutationFn: async (
      file: File
    ): Promise<{ id: string; filename: string }> => {
      if (file.size > MAX_ATTACHMENT_BYTES) throw new Error("file-too-large");
      const { id, upload_url: uploadUrl } = await inboxApi.requestChatUpload({
        filename: file.name,
        content_type: file.type,
        size: file.size
      });
      await inboxApi.uploadToPresignedUrl(uploadUrl, file);
      await inboxApi.confirmChatUpload(id);
      return { id, filename: file.name };
    }
  });
